import React, { useEffect, useState } from 'react';

const apiUrl = process.env.REACT_APP_API_URL;

const CastronegroAdmin = () => {
    const [players, setPlayers] = useState<any[]>([]);
    const [votes, setVotes] = useState<Record<string, number>>({});
    const [voteDetails, setVoteDetails] = useState<Record<string, string[]>>({});
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        try {
            const [playersRes, votesRes] = await Promise.all([
                fetch(`${apiUrl}:8061/cluedo/players`),
                fetch(`${apiUrl}:8061/cluedo/votes`)
            ]);

            if (playersRes.ok) {
                setPlayers(await playersRes.json());
            }
            if (votesRes.ok) {
                const data = await votesRes.json();
                setVotes(data.counts || {});
                setVoteDetails(data.details || {});
            }
        } catch (error) {
            console.error('Error fetching admin data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // Refresh every 5 seconds to see live votes
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, []);

    const resetVotes = async () => {
        if (!window.confirm("¿Estás seguro de que quieres borrar todos los votos? Esto no se puede deshacer.")) return;
        
        try {
            await fetch(`${apiUrl}:8061/cluedo/votes`, { method: 'DELETE' });
            fetchData();
        } catch (error) {
            console.error('Error resetting votes:', error);
        }
    };

    const killPlayer = async (qr: string) => {
        if (!window.confirm("¿Matar a este jugador?")) return;
        try {
            await fetch(`${apiUrl}:8061/cluedo/kill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ qr })
            });
            fetchData();
        } catch (error) {
            console.error('Error killing player:', error);
        }
    };

    const revivePlayer = async (qr: string) => {
        if (!window.confirm("¿Revivir a este jugador?")) return;
        try {
            await fetch(`${apiUrl}:8061/cluedo/revive`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ qr })
            });
            fetchData();
        } catch (error) {
            console.error('Error reviving player:', error);
        }
    };

    if (loading) return <div style={{ padding: '20px', color: '#fff', backgroundColor: '#121212', minHeight: '100vh' }}>Cargando Panel de Máster...</div>;

    // Sort players by vote count descending
    const sortedPlayers = [...players].sort((a, b) => {
        const votesA = votes[a.qr_code] || 0;
        const votesB = votes[b.qr_code] || 0;
        return votesB - votesA;
    });

    const totalVotes = Object.values(votes).reduce((sum, current) => sum + current, 0);

    return (
        <div style={{
            backgroundColor: '#121212',
            color: '#e0e0e0',
            minHeight: '100vh',
            fontFamily: 'sans-serif',
            padding: '20px',
            boxSizing: 'border-box'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #333', paddingBottom: '20px', marginBottom: '20px' }}>
                <h1 style={{ margin: 0, color: '#ff4c4c' }}>Panel del Máster - Castronegro</h1>
                <div>
                    <span style={{ marginRight: '20px', fontSize: '1.2rem' }}>Total Votos: <strong>{totalVotes}</strong></span>
                    <button
                        onClick={resetVotes}
                        style={{
                            backgroundColor: '#8b0000',
                            color: '#fff',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem'
                        }}
                    >
                        Resetear Votos
                    </button>
                </div>
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '20px'
            }}>
                {sortedPlayers.map(player => {
                    const isDead = player.titol?.startsWith('X ');
                    const displayTitle = isDead ? player.titol.substring(2) : player.titol;
                    const playerVotes = votes[player.qr_code] || 0;
                    
                    const playerVotesDetails = voteDetails[player.qr_code] || [];
                    const isWolfVictim = playerVotesDetails.some(entry => entry.endsWith('|MATAR_LOBO'));
                    
                    const annulledVoters = new Set<string>();
                    Object.keys(voteDetails).forEach(targetQr => {
                        const actions = voteDetails[targetQr];
                        actions.forEach(actStr => {
                            if (actStr.endsWith('|ANULAR')) annulledVoters.add(targetQr);
                        });
                    });

                    return (
                        <div key={player.qr_code} style={{
                            backgroundColor: isDead ? '#2a1a1a' : (isWolfVictim ? '#3a0000' : '#1a1a1a'),
                            border: `2px solid ${isDead ? '#ff3333' : (isWolfVictim ? '#ff0000' : '#444')}`,
                            borderRadius: '8px',
                            padding: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                            opacity: isDead ? 0.7 : 1,
                            boxShadow: isWolfVictim ? '0 0 15px rgba(255,0,0,0.5)' : 'none'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                <div>
                                    <h3 style={{ margin: '0 0 10px 0', fontSize: '1.5rem', color: isDead ? '#666' : '#f4e4bc' }}>
                                        {isDead ? `[FANTASMA] ${displayTitle}` : displayTitle}
                                    </h3>
                                    <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#ff4c4c' }}>
                                        {playerVotes} <span style={{ fontSize: '1.2rem', color: '#ccc', fontWeight: 'normal' }}>votos</span>
                                    </div>
                                    {playerVotesDetails.length > 0 && (
                                        <div style={{ fontSize: '1rem', color: '#aaa', marginTop: '10px', fontStyle: 'italic' }}>
                                            Votado por: {playerVotesDetails.map((entry, idx) => {
                                                const parts = entry.split('|');
                                                const qr = parts[0];
                                                const action = parts.length > 1 ? parts[1] : 'NORMAL';
                                                
                                                const voter = players.find(p => p.qr_code === qr);
                                                const name = voter ? (voter.titol.startsWith('X ') ? voter.titol.substring(2) : voter.titol) : qr;
                                                
                                                let actionColor = '#ccc';
                                                if (action === 'SALVAR') actionColor = '#4caf50'; // Verde
                                                else if (action.startsWith('MATAR')) actionColor = '#ff5252'; // Rojo
                                                else if (action === 'ANULAR') actionColor = '#ffb74d'; // Naranja
                                                
                                                const isVoterAnnulled = action === 'NORMAL' && annulledVoters.has(qr);

                                                return (
                                                    <span key={qr + idx} style={{ textDecoration: isVoterAnnulled ? 'line-through' : 'none', opacity: isVoterAnnulled ? 0.5 : 1 }}>
                                                        {name}
                                                        {action !== 'NORMAL' && <span style={{ color: actionColor, fontWeight: 'bold', textDecoration: 'none' }}> ({action})</span>}
                                                        {idx < playerVotesDetails.length - 1 ? ', ' : ''}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <small style={{ color: '#aaa' }}>{player.qr_code}</small>
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
                                {!isDead ? (
                                    <button 
                                        onClick={() => killPlayer(player.qr_code)}
                                        style={{ flex: 1, padding: '8px', backgroundColor: '#8b0000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        Matar
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => revivePlayer(player.qr_code)}
                                        style={{ flex: 1, padding: '8px', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        Revivir
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CastronegroAdmin;
